import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { useGlobalContext } from 'GlobalContext';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import { useEffect, useState } from 'react';
import { FaArrowRight } from 'react-icons/fa6';
import { IoSearchOutline } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { updateAppInfo } from 'services/app';
import { AppInfoRequestData } from 'services/interfaces/app';
import { SearchResponse } from 'services/interfaces/search';
import { search as searchCustomers } from 'services/search';
import { formatDocument } from 'shared/helpers/document';
import { useDebounce } from 'shared/hooks/useDebounce';
import { useAppTabContext } from '../AppContext';

interface TransferInstallationModalProps {
  isOpen: boolean;
  onClose(): void;
}

const TransferInstallationModal: React.FC<TransferInstallationModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { confirm } = useGlobalContext();
  const { app } = useAppTabContext();
  const { info, loadApps } = useCustomerContext();

  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState<SearchResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<SearchResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const isFiscalActive = app.fiscal === 1;
  const [changeCnpj, setChangeCnpj] = useState(!isFiscalActive);

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgCard = useColorModeValue('gray.50', 'gray.700');
  const hoverColor = useColorModeValue('gray.100', 'gray.600');

  const search = (query: string) => {
    if (!query) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    searchCustomers(query)
      .then((response) => {
        setResults(response.data);
      })
      .catch(() => {
        toast.error('Não foi possível buscar os clientes');
      })
      .finally(() => setLoading(false));
  };

  const debouncedSearch = useDebounce(search, 300);

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchText(e.target.value);
    setSelectedCustomer(null);
    debouncedSearch(e.target.value);
  };

  const handleSelectCustomer = (customer: SearchResponse) => {
    if (customer.document === info.document) {
      toast.warning('Não é possível transferir para o mesmo cliente');
      return;
    }
    setSelectedCustomer(customer);
    setResults([]);
    setSearchText('');
  };

  const handleSubmit = () => {
    if (!selectedCustomer) return;

    confirm(
      'Transferir Instalação',
      `Deseja transferir a instalação IDI ${app.idi} para o cliente ${selectedCustomer.name}?`,
      () => {
        setSubmitting(true);

        const payload: AppInfoRequestData = {
          oldCustomerId: info.customer_id,
          newCustomerId: selectedCustomer.customer_id,
          oldYoogaCustomerId: app.yoogaCustomerId,
          newYoogaCustomerId: selectedCustomer.yooga_customer_id,
          isInstallationMigration: true,
          ...(changeCnpj && {
            oldCnpj: info.document,
            newCnpj: selectedCustomer.document,
          }),
        };

        updateAppInfo(app.idi, payload)
          .then(() => {
            toast.success('Instalação transferida com sucesso');
            loadApps();
            onClose();
          })
          .catch(() => {
            toast.error('Falha ao transferir a instalação');
          })
          .finally(() => setSubmitting(false));
      },
    );
  };

  useEffect(() => {
    if (!isOpen) {
      setSearchText('');
      setResults([]);
      setSelectedCustomer(null);
      setLoading(false);
      setSubmitting(false);
      setChangeCnpj(!isFiscalActive);
    }
  }, [isOpen, isFiscalActive]);

  return (
    <Modal
      closeOnOverlayClick={!submitting}
      closeOnEsc={!submitting}
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
    >
      <ModalOverlay />

      <ModalContent>
        <ModalCloseButton isDisabled={submitting} />
        <ModalHeader>Transferir Instalação (IDI: {app.idi})</ModalHeader>

        <ModalBody>
          <Flex
            gap="1rem"
            align="stretch"
            direction={{ base: 'column', md: 'row' }}
          >
            <Box
              flex="1"
              p="1rem"
              borderRadius="md"
              border="1px solid"
              borderColor={borderColor}
              bg={bgCard}
            >
              <Text fontWeight="600" mb="0.5rem" fontSize="sm">
                Cliente Atual
              </Text>
              <Stack gap="0.25rem">
                <Text fontWeight="400" fontSize="sm">
                  {info.name}
                </Text>
                <Text fontWeight="400" fontSize="sm">
                  {info.trade_name}
                </Text>
                <Text fontWeight="400" fontSize="sm">
                  {formatDocument(info.document)}
                </Text>
              </Stack>
            </Box>

            <Flex
              align="center"
              justify="center"
              px="0.5rem"
              direction="column"
              gap="0.5rem"
            >
              <Text fontWeight="600" fontSize="sm" color="yooga.500">
                IDI: {app.idi}
              </Text>
              <Icon as={FaArrowRight} boxSize="1.5rem" color="gray.400" />
            </Flex>

            <Box
              flex="1"
              p="1rem"
              borderRadius="md"
              border="1px solid"
              borderColor={borderColor}
            >
              <Text fontWeight="600" mb="0.5rem" fontSize="sm">
                Cliente Destino
              </Text>

              {!selectedCustomer ? (
                <Stack>
                  <InputGroup size="sm">
                    <InputLeftElement>
                      <IoSearchOutline />
                    </InputLeftElement>
                    <Input
                      placeholder="Buscar cliente..."
                      value={searchText}
                      onChange={onChange}
                      isDisabled={submitting}
                    />
                  </InputGroup>

                  {loading && (
                    <Stack gap="0.5rem" mt="0.5rem">
                      <Skeleton height="2rem" />
                      <Skeleton height="2rem" />
                      <Skeleton height="2rem" />
                    </Stack>
                  )}

                  {!loading && results.length > 0 && (
                    <Stack
                      gap="0"
                      maxH="200px"
                      overflowY="auto"
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="md"
                      mt="0.5rem"
                    >
                      {results.slice(0, 5).map((result, idx) => (
                        <Box
                          key={idx}
                          p="0.5rem"
                          cursor="pointer"
                          _hover={{ bg: hoverColor }}
                          onClick={() => handleSelectCustomer(result)}
                          borderBottom={
                            idx < results.slice(0, 5).length - 1
                              ? '1px solid'
                              : 'none'
                          }
                          borderColor={borderColor}
                        >
                          <Text fontWeight="500" fontSize="xs">
                            {result.name}
                          </Text>
                          <Text fontWeight="300" fontSize="xs" color="gray.500">
                            {formatDocument(result.document)}
                          </Text>
                        </Box>
                      ))}
                    </Stack>
                  )}

                  {!loading && searchText && results.length === 0 && (
                    <Text fontSize="sm" color="gray.500" mt="0.5rem">
                      Nenhum cliente encontrado
                    </Text>
                  )}
                </Stack>
              ) : (
                <Stack gap="0.25rem">
                  <Text fontWeight="400" fontSize="sm">
                    {selectedCustomer.name}
                  </Text>
                  <Text fontWeight="400" fontSize="sm">
                    {selectedCustomer.trade_name}
                  </Text>
                  <Text fontWeight="400" fontSize="sm">
                    {formatDocument(selectedCustomer.document)}
                  </Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => setSelectedCustomer(null)}
                    mt="0.5rem"
                    alignSelf="flex-start"
                  >
                    Pesquisar novamente
                  </Button>
                </Stack>
              )}
            </Box>
          </Flex>

          <Divider my="1rem" />

          <Tooltip
            label="Não é possível alterar o documento pois o módulo fiscal está ativo"
            isDisabled={!isFiscalActive}
          >
            <Checkbox
              isChecked={changeCnpj}
              onChange={(e) => setChangeCnpj(e.target.checked)}
              isDisabled={submitting || isFiscalActive}
            >
              <Text fontSize="sm">
                Quero alterar o documento da instalação para o mesmo do cliente
                destino!
              </Text>
            </Checkbox>
          </Tooltip>
        </ModalBody>

        <ModalFooter>
          <HStack>
            <Button variant="ghost" onClick={onClose} isDisabled={submitting}>
              Cancelar
            </Button>
            <Button
              colorScheme="red"
              onClick={handleSubmit}
              isLoading={submitting}
              isDisabled={!selectedCustomer}
            >
              Transferir
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TransferInstallationModal;
